import { action, getSnapshot, Instance, isStateTreeNode, register, SnapshotOut, types } from "@gadgetinc/mobx-quick-tree";
import { GadgetDomainModelClass } from "../utils";

export enum ShopifyAppType {
  Public = "Public",
  Custom = "Custom",
}

export const LogoShapeStyles = ["circle", "square", "rounded-square", "hexagon"] as const;
export const LogoFontStyles = ["playful", "modern", "corporate", "elegant", "techy", "quirky"] as const;

export const AssistantOnboardingTypes = ["shopify", "web", "summon", "customize-template", "chatgpt"] as const;
export type AssistantOnboardingType = (typeof AssistantOnboardingTypes)[number];

export const AssistantExplicitGoals = ["none", "onboard:shopify", "onboard:web"] as const;
export type AssistantExplicitGoal = (typeof AssistantExplicitGoals)[number];

export type LogoShapeStyle = (typeof LogoShapeStyles)[number];
export type LogoFontStyle = (typeof LogoFontStyles)[number];

export type LogoStyle = {
  shape: LogoShapeStyle;
  font: LogoFontStyle;
  icon: {
    library: "lucide";
    name: string;
  };
  appName: string;
};

/**
 * Represents the metadata generated by the assistant
 */
@register
export class AssistantMetadata extends GadgetDomainModelClass("AssistantMetadata", {
  description: types.maybeNull(types.string),
  summary: types.maybeNull(types.string),
  frontendPages: types.maybeNull(types.frozen<Record<string, any>[]>()),
  modelMetadata: types.maybeNull(types.frozen<Record<string, any>[]>()),
  summonRequirementResult: types.maybeNull(types.frozen<Record<string, any>>()),
  summonSummary: types.maybeNull(types.string),
  onboardingConversationKey: types.maybeNull(types.string),
  shopifyAppType: types.maybeNull(types.enumeration<ShopifyAppType>("ShopifyAppType", Object.values(ShopifyAppType))),
}) {
  @action
  setDescription(description: string) {
    this.description = description;
  }

  @action
  setSummary(summary: string) {
    this.summary = summary;
  }

  @action
  setFrontendPages(frontendPages: Record<string, any>[] | null) {
    this.frontendPages = frontendPages;
  }

  @action
  setModelMetadata(modelMetadata: Record<string, any>[] | null) {
    this.modelMetadata = modelMetadata;
  }

  @action
  setSummonRequirementResult(summonRequirementResult: Record<string, any> | null) {
    this.summonRequirementResult = summonRequirementResult;
  }

  @action
  setSummonSummary(summonSummary: string | null) {
    this.summonSummary = summonSummary;
  }

  @action
  setOnboardingConversationKey(onboardingConversationKey: string | null) {
    this.onboardingConversationKey = onboardingConversationKey;
  }

  @action
  setShopifyAppType(shopifyAppType: ShopifyAppType | null) {
    this.shopifyAppType = shopifyAppType;
  }
}

export const assistantMetadataSnapshot = (
  assistantMetadata: Instance<typeof AssistantMetadata> | SnapshotOut<typeof AssistantMetadata>
) => {
  const snapshot = isStateTreeNode(assistantMetadata) ? getSnapshot(assistantMetadata) : assistantMetadata;

  return {
    ...snapshot,
    onboardingConversationKey: snapshot.onboardingConversationKey ?? (snapshot as any).shopifyOnboardingConversationKey,
  };
};

export const appDescriptionAndSummary = (description: string, summary: string) => {
  return `
${description}

<summary>
${summary}
</summary>
`;
};
